import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sun, CloudRain, Leaf, Snowflake } from 'lucide-react';

const seasons = [
  {
    id: 'pre-monsoon',
    name: 'Pre-Monsoon',
    months: 'March to May',
    icon: Sun,
    color: '#D97706',
    bgColor: '#FEF3C7',
    description: 'Hot and dry season, time to prepare for rains',
    activities: {
      plant: [
        'Prepare nursery beds',
        'Start drumstick (moringa) seeds',
        'Sow sunflower and maize',
        'Plant mango grafts'
      ],
      observe: [
        'Soil moisture levels',
        'Existing water bodies',
        'Wildlife patterns',
        'Wind direction'
      ],
      harvest: [
        'Jackfruit',
        'Mango (early varieties)',
        'Cashew nuts',
        'Tamarind'
      ]
    }
  },
  {
    id: 'monsoon',
    name: 'Monsoon',
    months: 'June to September',
    icon: CloudRain,
    color: '#1A4D2E',
    bgColor: '#D1FAE5',
    description: 'Western Ghats receive 2000 to 5000mm rainfall, peak planting season',
    activities: {
      plant: [
        'All forest tree saplings',
        'Paddy transplanting',
        'Areca and coconut',
        'Coffee and pepper',
        'Turmeric and ginger'
      ],
      observe: [
        'Water flow patterns',
        'Erosion points',
        'Swale effectiveness',
        'Frog and insect activity'
      ],
      harvest: [
        'Wild mushrooms',
        'Bamboo shoots',
        'Colocasia leaves',
        'Wild greens (kere soppu)'
      ]
    }
  },
  {
    id: 'post-monsoon',
    name: 'Post-Monsoon',
    months: 'October to November',
    icon: Leaf,
    color: '#A0522D',
    bgColor: '#FED7AA',
    description: 'Retreating monsoon, second planting window',
    activities: {
      plant: [
        'Ragi and millets',
        'Pulses (toor, horse gram)',
        'Cover crops',
        'Vegetable transplants'
      ],
      observe: [
        'Soil saturation levels',
        'Successful plantings',
        'Pest emergence',
        'Bird migration'
      ],
      harvest: [
        'Paddy',
        'Late mangoes',
        'Guava',
        'Coffee (arabica)'
      ]
    }
  },
  {
    id: 'winter',
    name: 'Winter',
    months: 'December to February',
    icon: Snowflake,
    color: '#6366F1',
    bgColor: '#E0E7FF',
    description: 'Cool and dry, maintenance and earthwork season',
    activities: {
      plant: [
        'Vegetables (tomato, brinjal)',
        'Wheat and oats',
        'Fruit tree pruning',
        'Green manure crops'
      ],
      observe: [
        'Groundwater levels',
        'Tree establishment',
        'Mulch decomposition',
        'Wildlife corridors'
      ],
      harvest: [
        'Coffee (robusta)',
        'Pepper',
        'Cardamom',
        'Oranges and citrus',
        'Arecanut'
      ]
    }
  }
];

export const KarnatakaSeasonalWheel = () => {
  const [activeSeason, setActiveSeason] = useState(null);
  const [activeTab, setActiveTab] = useState('plant');

  const currentSeason = activeSeason ? seasons.find(s => s.id === activeSeason) : null;

  return (
    <div className="flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-16" data-testid="seasonal-wheel">
      {/* Wheel Container */}
      <div className="relative flex-shrink-0">
        {/* Season Buttons - Cleaner grid layout */}
        <div className="grid grid-cols-2 gap-4 w-72">
          {seasons.map((season) => {
            const isActive = activeSeason === season.id;
            const Icon = season.icon;
            
            return (
              <motion.button
                key={season.id}
                onClick={() => setActiveSeason(isActive ? null : season.id)}
                className={`relative p-6 rounded-2xl border-2 transition-all duration-300 ${
                  isActive 
                    ? 'border-transparent shadow-lg' 
                    : 'border-transparent hover:border-current/20'
                }`}
                style={{ 
                  backgroundColor: isActive ? season.color : season.bgColor,
                  color: isActive ? 'white' : season.color
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid={`season-${season.id}`}
              >
                <Icon className="w-8 h-8 mx-auto mb-3" />
                <p className="font-serif text-sm font-medium">{season.name}</p>
                <p className={`text-xs mt-1 ${isActive ? 'text-white/80' : 'text-current/60'}`}>
                  {season.months}
                </p>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Info Panel */}
      <div className="flex-1 max-w-md w-full">
        <AnimatePresence mode="wait">
          {currentSeason ? (
            <motion.div
              key={currentSeason.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl p-6 lg:p-8 shadow-lg border border-border/30"
            >
              <div className="flex items-center gap-4 mb-6">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: currentSeason.bgColor }}
                >
                  <currentSeason.icon className="w-6 h-6" style={{ color: currentSeason.color }} />
                </div>
                <div>
                  <h3 className="font-serif text-xl lg:text-2xl" style={{ color: currentSeason.color }}>
                    {currentSeason.name}
                  </h3>
                  <p className="text-foreground/60 text-sm">{currentSeason.months}</p>
                </div>
              </div>

              <p className="text-foreground/70 text-sm mb-6">{currentSeason.description}</p>

              {/* Tabs */}
              <div className="flex gap-2 mb-6">
                {['plant', 'observe', 'harvest'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-2 rounded-full text-xs font-medium capitalize transition-all ${
                      activeTab === tab
                        ? 'text-white'
                        : 'text-foreground/60 bg-sand hover:bg-sand/80'
                    }`}
                    style={{
                      backgroundColor: activeTab === tab ? currentSeason.color : undefined
                    }}
                    data-testid={`tab-${tab}`}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {/* Activity List */}
              <ul className="space-y-2">
                {currentSeason.activities[activeTab].map((item, idx) => (
                  <motion.li
                    key={idx}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className="flex items-center gap-3 text-sm text-foreground/70"
                  >
                    <span 
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: currentSeason.color }}
                    />
                    {item}
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-sand rounded-2xl p-8 text-center"
            >
              <p className="text-foreground/60 mb-2">Click a season to explore</p>
              <h3 className="font-serif text-xl text-forest">Karnataka Seasonal Calendar</h3>
              <p className="text-sm text-foreground/50 mt-2">
                Hyperlocal planting guide for the Western Ghats
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
